import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import DevicesRoundedIcon from "@mui/icons-material/DevicesRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import LayersRoundedIcon from "@mui/icons-material/LayersRounded";
import { useAuth } from "../../../auth/useAuth";
import { can } from "../../../auth/can";

import { APP_API_BASE } from "../../../config/api";
const API_BASE =
  process.env.REACT_APP_API_BASE_URL?.replace(/\/$/, "") || `${APP_API_BASE}`;

const cardTone = {
  slate: { bg: "#f8fafc", iconBg: "#e2e8f0", iconColor: "#334155" },
  blue: { bg: "#f8fbff", iconBg: "#dbeafe", iconColor: "#1d4ed8" },
  green: { bg: "#f7fef9", iconBg: "#dcfce7", iconColor: "#15803d" },
  amber: { bg: "#fffaf5", iconBg: "#ffedd5", iconColor: "#c2410c" },
};

function SummaryCard({ icon, label, value, helper, tone = "slate" }) {
  const selected = cardTone[tone] || cardTone.slate;
  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        border: "1px solid rgba(15,23,42,0.08)",
        background: selected.bg,
        boxShadow: "0 10px 30px rgba(15,23,42,0.04)",
        height: "100%",
      }}
    >
      <CardContent sx={{ p: 2.25 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 46,
              height: 46,
              borderRadius: 2.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: selected.iconBg,
              color: selected.iconColor,
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
          <Box>
            <Typography
              variant="body2"
              sx={{ color: "#667085", fontWeight: 700 }}
            >
              {label}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 800, color: "#111827" }}>
              {value}
            </Typography>
            {helper ? (
              <Typography variant="caption" sx={{ color: "#667085" }}>
                {helper}
              </Typography>
            ) : null}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-IN", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}

export default function OrganizationLicensingTab({ orgId }) {
  const { activeOrg, permissions } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [plans, setPlans] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [summary, setSummary] = useState(null);
  const [creatingSubscription, setCreatingSubscription] = useState(false);
  const [creatingLicense, setCreatingLicense] = useState(false);
  const [subscriptionForm, setSubscriptionForm] = useState({
    plan_id: "",
    quantity: 1,
    billing_cycle: "monthly",
    unit_price: "",
    status: "active",
  });
  const [licenseForm, setLicenseForm] = useState({
    plan_id: "",
    device_type_id: "",
    quantity_purchased: 1,
    pricing_model: "per_device",
    unit_price: "",
    batch_code: "",
    status: "active",
  });

  const headers = useMemo(() => {
    const token = localStorage.getItem("authToken");
    return {
      Authorization: `Bearer ${token}`,
      ...(activeOrg?.id ? { "X-Org-ID": activeOrg.id } : {}),
    };
  }, [activeOrg]);

  const canManage = can(permissions, "organization.update");
  const canViewDeviceTypes = can(permissions, "device.view");

  const loadData = async () => {
    if (!orgId) return;
    try {
      setLoading(true);
      setError("");
      const requestEntries = [
        [
          "entitlements",
          axios.get(`${API_BASE}/api/v1/licensing/entitlements/current`, {
            headers,
            params: { organization_id: orgId },
          }),
        ],
        ["plans", axios.get(`${API_BASE}/api/v1/licensing/plans`, { headers })],
        [
          "subscriptions",
          axios.get(`${API_BASE}/api/v1/licensing/subscriptions`, {
            headers,
            params: { organization_id: orgId },
          }),
        ],
        [
          "licenses",
          axios.get(`${API_BASE}/api/v1/licensing/licenses`, {
            headers,
            params: { organization_id: orgId },
          }),
        ],
      ];

      if (canViewDeviceTypes) {
        requestEntries.push([
          "deviceTypes",
          axios.get(`${API_BASE}/api/v1/device-types`, { headers }),
        ]);
      }

      const settled = await Promise.allSettled(
        requestEntries.map((entry) => entry[1]),
      );
      const results = Object.fromEntries(
        requestEntries.map(([key], index) => [key, settled[index]]),
      );

      const requiredKeys = [
        "entitlements",
        "plans",
        "subscriptions",
        "licenses",
      ];
      const failedRequired = requiredKeys.find(
        (key) => results[key]?.status !== "fulfilled",
      );

      if (failedRequired) {
        const failure = results[failedRequired]?.reason;
        throw failure;
      }

      setSummary(results.entitlements.value.data?.summary || null);
      setPlans(
        Array.isArray(results.plans.value.data) ? results.plans.value.data : [],
      );
      setSubscriptions(
        Array.isArray(results.subscriptions.value.data)
          ? results.subscriptions.value.data
          : [],
      );
      setLicenses(
        Array.isArray(results.licenses.value.data)
          ? results.licenses.value.data
          : [],
      );
      setDeviceTypes(
        results.deviceTypes?.status === "fulfilled" &&
          Array.isArray(results.deviceTypes.value.data)
          ? results.deviceTypes.value.data
          : [],
      );
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          "Failed to load billing and licensing data",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [orgId, headers, canViewDeviceTypes]);

  const planNameById = useMemo(
    () => Object.fromEntries(plans.map((plan) => [plan.id, plan.name])),
    [plans],
  );
  const deviceTypeNameById = useMemo(
    () => Object.fromEntries(deviceTypes.map((type) => [type.id, type.name])),
    [deviceTypes],
  );

  const handleCreateSubscription = async () => {
    try {
      setCreatingSubscription(true);
      setError("");
      setSuccess("");
      await axios.post(
        `${API_BASE}/api/v1/licensing/subscriptions`,
        {
          organization_id: orgId,
          plan_id: subscriptionForm.plan_id || null,
          quantity: Number(subscriptionForm.quantity || 1),
          billing_cycle: subscriptionForm.billing_cycle,
          unit_price:
            subscriptionForm.unit_price === ""
              ? undefined
              : Number(subscriptionForm.unit_price),
          status: subscriptionForm.status,
        },
        { headers },
      );
      setSuccess("Subscription created successfully.");
      setSubscriptionForm({
        plan_id: "",
        quantity: 1,
        billing_cycle: "monthly",
        unit_price: "",
        status: "active",
      });
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to create subscription");
    } finally {
      setCreatingSubscription(false);
    }
  };

  const handleCreateLicense = async () => {
    try {
      setCreatingLicense(true);
      setError("");
      setSuccess("");
      await axios.post(
        `${API_BASE}/api/v1/licensing/licenses`,
        {
          organization_id: orgId,
          plan_id: licenseForm.plan_id || null,
          device_type_id: licenseForm.device_type_id || null,
          quantity_purchased: Number(licenseForm.quantity_purchased || 1),
          pricing_model: licenseForm.pricing_model,
          unit_price:
            licenseForm.unit_price === ""
              ? undefined
              : Number(licenseForm.unit_price),
          batch_code: licenseForm.batch_code || null,
          status: licenseForm.status,
        },
        { headers },
      );
      setSuccess("License pool created successfully.");
      setLicenseForm({
        plan_id: "",
        device_type_id: "",
        quantity_purchased: 1,
        pricing_model: "per_device",
        unit_price: "",
        batch_code: "",
        status: "active",
      });
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to create license pool");
    } finally {
      setCreatingLicense(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      {error ? <Alert severity="error">{error}</Alert> : null}
      {success ? <Alert severity="success">{success}</Alert> : null}

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box
          sx={{
            width: {
              xs: "100%",
              sm: "calc(50% - 8px)",
              xl: "calc(25% - 12px)",
            },
          }}
        >
          <SummaryCard
            icon={<CreditCardRoundedIcon />}
            label="Active Subscriptions"
            value={summary?.active_subscriptions ?? subscriptions.length}
            helper="Recurring commercial plans"
            tone="blue"
          />
        </Box>

        <Box
          sx={{
            width: {
              xs: "100%",
              sm: "calc(50% - 8px)",
              xl: "calc(25% - 12px)",
            },
          }}
        >
          <SummaryCard
            icon={<LayersRoundedIcon />}
            label="Active License Pools"
            value={summary?.active_licenses ?? licenses.length}
            helper="Sell per device or by lot"
            tone="amber"
          />
        </Box>

        <Box
          sx={{
            width: {
              xs: "100%",
              sm: "calc(50% - 8px)",
              xl: "calc(25% - 12px)",
            },
          }}
        >
          <SummaryCard
            icon={<DevicesRoundedIcon />}
            label="Subscription Capacity"
            value={summary?.subscription_capacity ?? 0}
            helper={`${summary?.available_subscription_slots ?? 0} slots available`}
            tone="green"
          />
        </Box>

        <Box
          sx={{
            width: {
              xs: "100%",
              sm: "calc(50% - 8px)",
              xl: "calc(25% - 12px)",
            },
          }}
        >
          <SummaryCard
            icon={<InsightsRoundedIcon />}
            label="License Availability"
            value={summary?.available_license_slots ?? 0}
            helper={`${summary?.active_devices ?? 0} active devices in org`}
          />
        </Box>
      </Box>

      {/* Plans + Overview */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
        <Box sx={{ width: { xs: "100%", xl: "calc(50% - 12px)" } }}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 3.5,
              border: "1px solid rgba(15,23,42,0.08)",
              boxShadow: "0 14px 34px rgba(15,23,42,0.04)",
              height: "100%",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack spacing={0.5} sx={{ mb: 2.5 }}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 800, color: "#111827" }}
                >
                  Subscription Plans
                </Typography>
                <Typography variant="body2" sx={{ color: "#667085" }}>
                  Current catalog available for this organization.
                </Typography>
              </Stack>

              <Stack spacing={1.5}>
                {plans.length ? (
                  plans.map((plan) => (
                    <Box
                      key={plan.id}
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        border: "1px solid rgba(15,23,42,0.08)",
                        backgroundColor: "#fbfcfe",
                      }}
                    >
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        justifyContent="space-between"
                        spacing={1}
                      >
                        <Box>
                          <Typography
                            sx={{ fontWeight: 800, color: "#111827" }}
                          >
                            {plan.name}
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#667085" }}>
                            {plan.code} | {plan.billing_model} | {plan.interval}
                          </Typography>
                        </Box>
                        <Chip
                          label={`${plan.currency || "INR"} ${plan.unit_price ?? 0}`}
                          sx={{ alignSelf: "flex-start", fontWeight: 700 }}
                        />
                      </Stack>
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" sx={{ color: "#667085" }}>
                    No plans found yet.
                  </Typography>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ width: { xs: "100%", xl: "calc(50% - 12px)" } }}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 3.5,
              border: "1px solid rgba(15,23,42,0.08)",
              boxShadow: "0 14px 34px rgba(15,23,42,0.04)",
              height: "100%",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack spacing={0.5} sx={{ mb: 2.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  Entitlement Overview
                </Typography>
                <Typography variant="body2" sx={{ color: "#667085" }}>
                  Current capacity and active device coverage for this tenant.
                </Typography>
              </Stack>

              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                <Box sx={{ width: { xs: "100%", sm: "calc(50% - 8px)" } }}>
                  <Box
                    sx={{ p: 2, borderRadius: 3, backgroundColor: "#f8fafc" }}
                  >
                    <Typography sx={{ color: "#667085", fontWeight: 700 }}>
                      Active Devices
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 800 }}>
                      {summary?.active_devices ?? 0}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ width: { xs: "100%", sm: "calc(50% - 8px)" } }}>
                  <Box
                    sx={{ p: 2, borderRadius: 3, backgroundColor: "#f8fafc" }}
                  >
                    <Typography sx={{ color: "#667085", fontWeight: 700 }}>
                      License Slots Available
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 800 }}>
                      {summary?.available_license_slots ?? 0}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
        <Box sx={{ width: { xs: "100%", xl: "calc(50% - 12px)" } }}>
          <Card
            elevation={0}
            sx={{ borderRadius: 3.5, border: "1px solid rgba(15,23,42,0.08)" }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack spacing={0.5} sx={{ mb: 2.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  Organization Subscriptions
                </Typography>
                <Typography variant="body2" sx={{ color: "#667085" }}>
                  Recurring SaaS plans currently attached to this org.
                </Typography>
              </Stack>

              <Stack spacing={1.5}>
                {subscriptions.length ? (
                  subscriptions.map((row) => (
                    <Box
                      key={row.id}
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        border: "1px solid rgba(15,23,42,0.08)",
                      }}
                    >
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        justifyContent="space-between"
                        spacing={1}
                      >
                        <Box>
                          <Typography sx={{ fontWeight: 800 }}>
                            {planNameById[row.plan_id] ||
                              row.price_snapshot?.plan_name ||
                              "Custom plan"}
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#667085" }}>
                            {row.billing_model} | {row.billing_cycle} | Qty{" "}
                            {row.quantity}
                          </Typography>
                        </Box>
                        <Chip
                          label={String(row.status || "active").toUpperCase()}
                          sx={{ alignSelf: "flex-start", fontWeight: 700 }}
                        />
                      </Stack>
                      <Typography
                        variant="caption"
                        sx={{ mt: 1.2, display: "block", color: "#667085" }}
                      >
                        Starts {formatDate(row.starts_at)} | Ends{" "}
                        {formatDate(row.ends_at)}
                      </Typography>
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" sx={{ color: "#667085" }}>
                    No subscriptions attached yet.
                  </Typography>
                )}
              </Stack>

              {canManage ? (
                <>
                  <Divider sx={{ my: 2.5 }} />
                  <Typography sx={{ fontWeight: 800, mb: 1.5 }}>
                    Create Subscription
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      flexWrap: "wrap",
                      "& > div": {
                        flex: {
                          xs: "1 1 100%",
                          sm: "1 1 calc(50% - 8px)",
                          md: "1 1 calc(25% - 12px)",
                        },
                      },
                    }}
                  >
                    <Box>
                      <FormControl fullWidth size="small">
                        <InputLabel>Plan</InputLabel>
                        <Select
                          label="Plan"
                          value={subscriptionForm.plan_id}
                          onChange={(e) =>
                            setSubscriptionForm((prev) => ({
                              ...prev,
                              plan_id: e.target.value,
                            }))
                          }
                        >
                          <MenuItem value="">No linked plan</MenuItem>
                          {plans.map((plan) => (
                            <MenuItem key={plan.id} value={plan.id}>
                              {plan.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    <Box>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Quantity"
                        value={subscriptionForm.quantity}
                        onChange={(e) =>
                          setSubscriptionForm((prev) => ({
                            ...prev,
                            quantity: e.target.value,
                          }))
                        }
                      />
                    </Box>

                    <Box>
                      <FormControl fullWidth size="small">
                        <InputLabel>Billing Cycle</InputLabel>
                        <Select
                          label="Billing Cycle"
                          value={subscriptionForm.billing_cycle}
                          onChange={(e) =>
                            setSubscriptionForm((prev) => ({
                              ...prev,
                              billing_cycle: e.target.value,
                            }))
                          }
                        >
                          <MenuItem value="monthly">Monthly</MenuItem>
                          <MenuItem value="quarterly">Quarterly</MenuItem>
                          <MenuItem value="yearly">Yearly</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>

                    <Box>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Unit Price"
                        value={subscriptionForm.unit_price}
                        onChange={(e) =>
                          setSubscriptionForm((prev) => ({
                            ...prev,
                            unit_price: e.target.value,
                          }))
                        }
                      />
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    onClick={handleCreateSubscription}
                    disabled={creatingSubscription}
                    sx={{
                      mt: 2,
                      borderRadius: 2.5,
                      textTransform: "none",
                      fontWeight: 700,
                    }}
                  >
                    {creatingSubscription
                      ? "Creating..."
                      : "Create Subscription"}
                  </Button>
                </>
              ) : null}
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ width: { xs: "100%", xl: "calc(50% - 12px)" } }}>
          <Card
            elevation={0}
            sx={{ borderRadius: 3.5, border: "1px solid rgba(15,23,42,0.08)" }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack spacing={0.5} sx={{ mb: 2.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  License Pools
                </Typography>
                <Typography variant="body2" sx={{ color: "#667085" }}>
                  Per-device or lot-based license pools available for
                  activation.
                </Typography>
              </Stack>

              <Stack spacing={1.5}>
                {licenses.length ? (
                  licenses.map((row) => (
                    <Box
                      key={row.id}
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        border: "1px solid rgba(15,23,42,0.08)",
                      }}
                    >
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        justifyContent="space-between"
                        spacing={1}
                      >
                        <Box>
                          <Typography sx={{ fontWeight: 800 }}>
                            {deviceTypeNameById[row.device_type_id] ||
                              row.batch_code ||
                              "Generic License Pool"}
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#667085" }}>
                            {row.pricing_model} | Purchased{" "}
                            {row.quantity_purchased} | Assigned{" "}
                            {row.quantity_assigned}
                          </Typography>
                        </Box>
                        <Chip
                          label={`${row.quantity_available} available`}
                          sx={{ alignSelf: "flex-start", fontWeight: 700 }}
                        />
                      </Stack>
                      <Typography
                        variant="caption"
                        sx={{ mt: 1.2, display: "block", color: "#667085" }}
                      >
                        Valid {formatDate(row.valid_from)} to{" "}
                        {formatDate(row.valid_to)}
                      </Typography>
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" sx={{ color: "#667085" }}>
                    No license pools created yet.
                  </Typography>
                )}
              </Stack>

              {canManage ? (
                <>
                  <Divider sx={{ my: 2.5 }} />
                  <Typography sx={{ fontWeight: 800, mb: 1.5 }}>
                    Create License Pool
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      flexWrap: "wrap",
                      "& > div": {
                        flex: {
                          xs: "1 1 100%",
                          sm: "1 1 calc(50% - 8px)",
                          md: "1 1 calc(25% - 12px)",
                        },
                      },
                    }}
                  >
                    <Box>
                      <FormControl fullWidth size="small">
                        <InputLabel>Linked Plan</InputLabel>
                        <Select
                          label="Linked Plan"
                          value={licenseForm.plan_id}
                          onChange={(e) =>
                            setLicenseForm((prev) => ({
                              ...prev,
                              plan_id: e.target.value,
                            }))
                          }
                        >
                          <MenuItem value="">No linked plan</MenuItem>
                          {plans.map((plan) => (
                            <MenuItem key={plan.id} value={plan.id}>
                              {plan.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                    <Box>
                      <FormControl fullWidth size="small">
                        <InputLabel>Device Type</InputLabel>
                        <Select
                          label="Device Type"
                          value={licenseForm.device_type_id}
                          onChange={(e) =>
                            setLicenseForm((prev) => ({
                              ...prev,
                              device_type_id: e.target.value,
                            }))
                          }
                        >
                          <MenuItem value="">Any device type</MenuItem>
                          {deviceTypes.map((type) => (
                            <MenuItem key={type.id} value={type.id}>
                              {type.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                    <Box>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Quantity"
                        value={licenseForm.quantity_purchased}
                        onChange={(e) =>
                          setLicenseForm((prev) => ({
                            ...prev,
                            quantity_purchased: e.target.value,
                          }))
                        }
                      />
                    </Box>
                    <Box>
                      <FormControl fullWidth size="small">
                        <InputLabel>Pricing Model</InputLabel>
                        <Select
                          label="Pricing Model"
                          value={licenseForm.pricing_model}
                          onChange={(e) =>
                            setLicenseForm((prev) => ({
                              ...prev,
                              pricing_model: e.target.value,
                            }))
                          }
                        >
                          <MenuItem value="per_device">Per Device</MenuItem>
                          <MenuItem value="lot">Lot</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                    <Box>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Unit Price"
                        value={licenseForm.unit_price}
                        onChange={(e) =>
                          setLicenseForm((prev) => ({
                            ...prev,
                            unit_price: e.target.value,
                          }))
                        }
                      />
                    </Box>
                    <Box>
                      <TextField
                        fullWidth
                        size="small"
                        label="Batch / Lot Code"
                        value={licenseForm.batch_code}
                        onChange={(e) =>
                          setLicenseForm((prev) => ({
                            ...prev,
                            batch_code: e.target.value,
                          }))
                        }
                      />
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    onClick={handleCreateLicense}
                    disabled={creatingLicense}
                    sx={{
                      mt: 2,
                      borderRadius: 2.5,
                      textTransform: "none",
                      fontWeight: 700,
                    }}
                  >
                    {creatingLicense ? "Creating..." : "Create License Pool"}
                  </Button>
                </>
              ) : null}
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Stack>
  );
}
